import { useNavigate } from 'react-router-dom';
import { Menu } from '@headlessui/react';
import {
  BellIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  Bars3Icon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';

interface HeaderProps {
  title?: string;
  onMenuClick: () => void;
}

export function Header({ title, onMenuClick }: HeaderProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="sticky top-0 z-20 bg-white border-b border-gray-200">
      <div className="flex items-center justify-between h-16 px-4 sm:px-6">
        {/* Mobile menu button and page title */}
        <div className="flex items-center gap-3">
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Bars3Icon className="w-6 h-6" />
          </button>
          {title && (
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">{title}</h1>
          )}
        </div>

        {/* Right side actions */}
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Notifications */}
          <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
            <BellIcon className="w-5 h-5" />
          </button>

          {/* User menu */}
          <Menu as="div" className="relative">
            <Menu.Button className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <div className="w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-semibold">
                  {user?.employee?.firstName?.[0] || user?.email?.[0]?.toUpperCase()}
                </span>
              </div>
              <span className="hidden sm:inline text-sm font-medium text-gray-700">
                {user?.employee?.firstName || user?.email?.split('@')[0]}
              </span>
            </Menu.Button>

            <Menu.Items className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 focus:outline-none z-50">
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => navigate('/profile')}
                    className={`${
                      active ? 'bg-gray-50' : ''
                    } flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700`}
                  >
                    <UserCircleIcon className="w-5 h-5" />
                    My Profile
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => navigate('/settings')}
                    className={`${
                      active ? 'bg-gray-50' : ''
                    } flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700`}
                  >
                    <Cog6ToothIcon className="w-5 h-5" />
                    Settings
                  </button>
                )}
              </Menu.Item>
              <div className="border-t border-gray-200 my-1" />
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={handleLogout}
                    className={`${
                      active ? 'bg-gray-50' : ''
                    } flex items-center gap-3 w-full px-4 py-2 text-sm text-danger-600`}
                  >
                    <ArrowRightOnRectangleIcon className="w-5 h-5" />
                    Logout
                  </button>
                )}
              </Menu.Item>
            </Menu.Items>
          </Menu>
        </div>
      </div>
    </header>
  );
}
